import { Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const TOC = [
  "introduction: the body keeps the receipts",
  "chapter 01: the optimization trap",
  "chapter 02: the respectability bargain",
  "chapter 03: desire is a design problem",
  "chapter 04: the locked pelvis",
  "chapter 05: the tease as self-practice",
  "chapter 06: dressing as dialogue",
  "chapter 07: the reveal",
  "chapter 08: attention as foreplay",
  "chapter 09: desiring the self",
  "chapter 10: the evidence file",
  "closing: the rebellion of pleasure",
  "sources and further reading",
];

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [freeLesson, setFreeLesson] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [guestEmail, setGuestEmail] = useState("");
  const emailRef = useRef(null);

  useEffect(() => {
    api.get("/lessons").then((r) => {
      const free = r.data.find((l) => l.is_free);
      setFreeLesson(free);
    }).catch(() => {});
  }, []);

  const startCheckout = async () => {
    // logged-in existing user path
    if (user) {
      if (user.has_purchased) { nav("/course"); return; }
      setBusy(true);
      try {
        const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
        window.location.href = data.checkout_url;
      } catch (e) {
        toast.error(e?.response?.data?.detail || "checkout failed");
        setBusy(false);
      }
      return;
    }
    // guest path — reveal email input then start checkout
    if (!showEmailForm) {
      setShowEmailForm(true);
      setTimeout(() => emailRef.current?.focus(), 60);
      return;
    }
    if (!guestEmail) { emailRef.current?.focus(); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout-guest", {
        email: guestEmail, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "checkout failed");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-24 md:pt-24 md:pb-32">
        <div className="grid md:grid-cols-5 gap-10 md:gap-16 items-center">
          <div className="md:col-span-3 space-y-8">
            <p className="text-xs uppercase tracking-[0.3em]" style={{ color: "var(--gold)" }}>
              a self-guided workshop
            </p>
            <h1
              className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
              style={{ color: "var(--ink)" }}
              data-testid="hero-title"
            >
              showgirl desire
            </h1>
            <p className="max-w-xl text-lg" style={{ color: "var(--ink-soft)" }}>
              showgirl desire is a thirteen-part self-guided workshop about the wanting
              you organized out of existence. the career, the household, the version of
              you that has it all together. somewhere in there your body went quiet.
              this is how it gets loud again. the respectability bargain is over.
              welcome back.
            </p>

            <div className="space-y-4 pt-2" data-testid="cta-block">
              {/* primary — enroll */}
              {showEmailForm && !user ? (
                <div className="flex flex-col sm:flex-row gap-3 max-w-lg" data-testid="guest-email-form">
                  <input
                    ref={emailRef}
                    type="email"
                    required
                    placeholder="your email for the receipt + access"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    className="field flex-1"
                    data-testid="guest-email-input"
                  />
                  <button
                    onClick={startCheckout}
                    disabled={busy || !guestEmail}
                    className="btn-primary"
                    data-testid="guest-checkout-submit"
                  >
                    {busy ? "opening stripe..." : "continue to payment"}
                  </button>
                </div>
              ) : (
                <button
                  onClick={startCheckout}
                  disabled={busy}
                  className="btn-primary"
                  data-testid="cta-buy"
                >
                  {user?.has_purchased ? "open the course" : busy ? "loading..." : "enroll  $150 cad"}
                </button>
              )}

              {/* secondary — read the free chapter */}
              <div className="flex flex-wrap items-center gap-4">
                {freeLesson && (
                  <Link
                    to={`/course/${freeLesson.id}`}
                    className="btn-ghost"
                    data-testid="cta-read-free"
                  >
                    read the free chapter
                  </Link>
                )}
                {!user && (
                  <Link
                    to="/login"
                    className="text-sm underline"
                    style={{ color: "var(--ink-soft)" }}
                    data-testid="cta-signin"
                  >
                    already enrolled? log in
                  </Link>
                )}
              </div>
            </div>

            <p className="text-xs pt-2" style={{ color: "var(--ink-soft)" }}>
              one payment. lifetime access. read on any device.
            </p>
          </div>
          <div className="md:col-span-2">
            <div
              className="aspect-[3/4] w-full overflow-hidden"
              style={{ border: "1px solid var(--gold-soft)" }}
            >
              <img
                src="https://customer-assets-4nw71qhi.emergentagent.net/job_desire-academy/artifacts/huvh16ne_2AAB723C-C5E2-4D5E-93BC-418CB0806E13.png"
                alt="showgirl desire"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* what's inside */}
      <section className="border-t" style={{ borderColor: "var(--gold-soft)" }}>
        <div className="max-w-4xl mx-auto px-6 py-20">
          <p className="text-xs uppercase tracking-[0.3em] mb-6" style={{ color: "var(--gold)" }}>
            what&apos;s inside
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl mb-10" style={{ color: "var(--ink)" }}>
            thirteen lessons. one long conversation.
          </h2>
          <ul className="space-y-3 text-base" data-testid="toc-list">
            {TOC.map((t, i) => (
              <li key={i} className="flex items-baseline gap-4" style={{ color: "var(--ink-soft)" }}>
                <span className="font-serif text-sm" style={{ color: "var(--gold)" }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <footer className="border-t py-10 text-center text-xs" style={{ borderColor: "var(--gold-soft)", color: "var(--ink-soft)" }}>
        showgirldesire.com  © {new Date().getFullYear()}
      </footer>
    </div>
  );
}
